import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { LemonadeAvatar } from './LemonadeAvatar';
import { Loader2, Mail } from 'lucide-react';

interface LemonadeLoginProps {
  onSwitchToSignup: () => void;
  onForgotPassword?: () => void;
}

export function LemonadeLogin({ onSwitchToSignup }: LemonadeLoginProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Allow Braze/marketing emails to deep-link past login by passing ?redirect=/dashboard/...
  const rawRedirect = searchParams.get('redirect');
  const safeRedirect = rawRedirect && rawRedirect.startsWith('/') && !rawRedirect.startsWith('//')
    ? rawRedirect
    : null;
  const postAuthPath = safeRedirect
    ?? (searchParams.get('source') === 'protect' ? '/dashboard/protect' : '/dashboard');
  const [stage, setStage] = useState<'email' | 'sent'>('email');
  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpCode, setOtpCode] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);

  // Countdown timer for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Listen for auth changes (user clicked magic link in another tab)
  useEffect(() => {
    if (stage !== 'sent') return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        navigate(postAuthPath);
      }
    });
    return () => subscription.unsubscribe();
  }, [stage, navigate, postAuthPath]);

  const handleSendLink = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!email.includes('@') || isLoading) return;

    setError(null);
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}${postAuthPath}`,
          shouldCreateUser: false, // Login only - don't create new users
        }
      });
      
      if (error) {
        // Handle case where user doesn't exist
        if (error.message.includes('Signups not allowed') || error.message.includes('User not found')) {
          throw new Error('No account found with this email. Please sign up first.');
        }
        throw error;
      }
      
      setStage('sent');
      setResendCooldown(60);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send link');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otpCode.length !== 6 || isLoading) return;

    setError(null);
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: otpCode,
        type: 'email'
      });
      
      if (error) throw error;
      
      navigate(postAuthPath);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-start pt-16 px-6">
      <LemonadeAvatar />

      <div className="w-full max-w-lg mt-8 text-center space-y-8">
        <div className="space-y-2">
          <h1 className="text-4xl lg:text-5xl font-light text-foreground">
            Welcome back!
          </h1>
          <p className="text-lg text-muted-foreground">
            {stage === 'email' ? 'Enter your email to sign in' : 'Check your email'}
          </p>
        </div>

        {stage === 'email' ? (
          <form onSubmit={handleSendLink} className="space-y-4">
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-4 rounded-lg border-2 border-border focus:border-primary focus:outline-none text-lg text-foreground placeholder-muted-foreground bg-background"
              autoFocus
            />
            
            {error && <p className="text-base text-destructive">{error}</p>}
            
            <button
              type="submit"
              disabled={!email.includes('@') || isLoading}
              className="w-full py-4 rounded-lg font-medium text-lg bg-primary text-primary-foreground disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Link'
              )}
            </button>
          </form>
        ) : (
          <div className="space-y-6">
            {/* Email icon */}
            <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
              <Mail className="w-8 h-8 text-primary" />
            </div>
            
            {!showOtpInput ? (
              <>
                <p className="text-muted-foreground">
                  We sent a sign-in link to{' '}
                  <span className="font-medium text-foreground">{email}</span>
                </p>
                
                <p className="text-sm text-muted-foreground">
                  Click the link in your email to continue
                </p>
                
                {/* Option to enter code manually */}
                <button
                  type="button"
                  onClick={() => setShowOtpInput(true)}
                  className="text-sm text-primary hover:underline"
                >
                  Or enter the 6-digit code
                </button>
              </>
            ) : (
              <form onSubmit={handleVerifyOtp} className="space-y-4 text-left">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">
                    Enter the 6-digit code from your email
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="w-full px-4 py-4 rounded-lg border-2 border-border focus:border-primary focus:outline-none text-2xl text-center tracking-[0.5em] font-mono text-foreground placeholder-muted-foreground bg-background"
                    autoFocus
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={otpCode.length !== 6 || isLoading}
                  className="w-full py-4 rounded-lg font-medium text-lg bg-primary text-primary-foreground disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </button>
                
                <button
                  type="button"
                  onClick={() => setShowOtpInput(false)}
                  className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Back to magic link
                </button>
              </form>
            )}
            
            {error && <p className="text-base text-destructive">{error}</p>}
            
            <div className="flex justify-center gap-4 text-sm">
              <button 
                onClick={() => { setStage('email'); setError(null); setShowOtpInput(false); setOtpCode(''); }} 
                className="text-muted-foreground hover:text-foreground transition-colors"
                disabled={isLoading}
              >
                Use different email
              </button>
              <button 
                onClick={handleSendLink} 
                disabled={resendCooldown > 0 || isLoading} 
                className="text-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend link'}
              </button>
            </div>
          </div>
        )}

        <p className="text-lg text-muted-foreground">
          Don't have an account?{' '}
          <button
            type="button"
            onClick={onSwitchToSignup}
            className="text-primary hover:underline font-medium"
          >
            Sign up
          </button>
        </p>
      </div>
    </div>
  );
}
